import classNames from "classnames";
import { useEffect, useId, useState } from "react";
import { type FieldError, type UseFormRegisterReturn } from "react-hook-form";

import { states } from "../../data/states";
import { useDebounce } from "../../hooks/use-debounce";
import { MIN_SCHOOL_SEARCH_LENGTH, useFindSchools } from "../../hooks/use-families";
import { SelectField } from "../forms/select-field";

export const schoolTypeOptions = [
  { label: "Public", value: "PU" },
  { label: "Parochial", value: "PA" },
  { label: "Private", value: "PR" },
  { label: "Charter", value: "CH" }
] as const;

interface SchoolLookupSectionProps {
  schoolNameValue: string;
  schoolNameRegister: UseFormRegisterReturn;
  schoolNameError?: FieldError;
  onSchoolSelect: (schoolName: string) => void;
  onSchoolCodeChange: (schoolCode: string) => void;
  onSchoolNameValidationReset: () => void;
  disabled?: boolean;
}

const formatSelectedSchoolLabel = ({ name }: { name: string }) => name;

const formatSchoolSecondaryLabel = ({
  address,
  city,
  state,
  zipcode
}: {
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}) => ({
  addressLine: address?.trim() ?? "",
  cityStateZip: [city, [state, zipcode].filter(Boolean).join(" ")].filter(Boolean).join(", ")
});

export const SchoolLookupSection = ({
  schoolNameValue,
  schoolNameRegister,
  schoolNameError,
  onSchoolSelect,
  onSchoolCodeChange,
  onSchoolNameValidationReset,
  disabled = false
}: SchoolLookupSectionProps) => {
  const [schoolType, setSchoolType] = useState("");
  const [schoolState, setSchoolState] = useState("");
  const [isSchoolResultsOpen, setIsSchoolResultsOpen] = useState(false);
  const [isSchoolSelectedFromResults, setIsSchoolSelectedFromResults] = useState(false);
  const schoolNameId = useId();
  const isSchoolNameDisabled = disabled || !schoolType || !schoolState;

  const schoolName = schoolNameValue ?? "";
  const debouncedSchoolSearch = useDebounce(schoolName, 350);
  const { schools, isLoading } = useFindSchools({
    search: debouncedSchoolSearch,
    state: schoolState,
    schoolType
  });
  const hasSearchTerm = schoolName.trim().length >= MIN_SCHOOL_SEARCH_LENGTH;

  useEffect(() => {
    if (isSchoolNameDisabled) {
      setIsSchoolResultsOpen(false);
    }
  }, [isSchoolNameDisabled]);

  const shouldShowResults = isSchoolResultsOpen && hasSearchTerm && !isSchoolNameDisabled;
  const shouldShowMinSearchMessage =
    isSchoolResultsOpen && !hasSearchTerm && schoolName.length > 0 && !isSchoolNameDisabled;
  const shouldShowNoMatchesMessage =
    hasSearchTerm &&
    !isLoading &&
    schools.length === 0 &&
    !isSchoolNameDisabled &&
    schoolName.length > 0 &&
    !isSchoolSelectedFromResults;

  return (
    <div className="card shaded mb-0">
      <div className="card-body py-0">
        <h4 className="card-subheader">School Lookup</h4>
        <p className="form-helper mb-2">
          Enter a School Name (at least {MIN_SCHOOL_SEARCH_LENGTH} characters) to locate the correct school. Use School
          Type and School State as needed to help narrow your search.
        </p>
        <div className="row form-standard gap-0">
          <div className="col col-12 col-lg-6">
            <SelectField
              disableColClass={true}
              label="School Type"
              disabled={disabled}
              value={schoolType}
              onChange={event => {
                setSchoolType(event.target.value);
                onSchoolNameValidationReset();
                if (isSchoolSelectedFromResults && schoolName.length > 0) {
                  onSchoolCodeChange("");
                  onSchoolSelect("");
                }
                setIsSchoolSelectedFromResults(false);
              }}
            >
              <option value="">Select Type</option>
              {schoolTypeOptions.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="col col-12 col-lg-6">
            <SelectField
              disableColClass={true}
              label="School State"
              disabled={disabled}
              value={schoolState}
              onChange={event => {
                setSchoolState(event.target.value);
                onSchoolNameValidationReset();
                if (isSchoolSelectedFromResults && schoolName.length > 0) {
                  onSchoolCodeChange("");
                  onSchoolSelect("");
                }
                setIsSchoolSelectedFromResults(false);
              }}
            >
              <option value="">Select State</option>
              {states.map(({ abbreviation, name }) => (
                <option key={abbreviation} value={abbreviation}>
                  {name}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="col col-12">
            <div className="dropdown">
              <div className="form-floating has-icon school-lookup-name-field">
                <input
                  id={schoolNameId}
                  type="text"
                  className={classNames("form-control", { "is-invalid": schoolNameError })}
                  placeholder="School Name"
                  autoComplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={schoolNameError ? "true" : "false"}
                  {...schoolNameRegister}
                  disabled={isSchoolNameDisabled}
                  onFocus={() => setIsSchoolResultsOpen(true)}
                  onBlur={event => {
                    schoolNameRegister.onBlur(event);
                    setIsSchoolResultsOpen(false);
                  }}
                  onChange={event => {
                    onSchoolCodeChange("");
                    onSchoolNameValidationReset();
                    schoolNameRegister.onChange(event);
                    setIsSchoolSelectedFromResults(false);
                    setIsSchoolResultsOpen(true);
                  }}
                />
                <label htmlFor={schoolNameId}>School Name</label>
                <span className="fas fa-search" aria-hidden="true"></span>
              </div>

              {shouldShowResults && (isLoading || schools.length > 0) && (
                <div className="dropdown-menu scrollable faux-select full show">
                  <ul>
                    {isLoading ? (
                      <li>
                        <button className="btn" type="button" disabled>
                          Loading...
                        </button>
                      </li>
                    ) : (
                      schools.map(school => {
                        const secondaryLabel = formatSchoolSecondaryLabel(school);

                        return (
                          <li key={school.schoolCode}>
                            <button
                              className="btn"
                              type="button"
                              onMouseDown={event => {
                                event.preventDefault();
                                onSchoolCodeChange(school.schoolCode);
                                onSchoolSelect(formatSelectedSchoolLabel(school));
                                setIsSchoolSelectedFromResults(true);
                                setIsSchoolResultsOpen(false);
                              }}
                            >
                              {school.name}
                              <span className="secondary">
                                {secondaryLabel.addressLine}
                                <br />
                                {secondaryLabel.cityStateZip}
                              </span>
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              )}

              {shouldShowMinSearchMessage && (
                <div className="dropdown-menu scrollable faux-select full show">
                  <ul>
                    <li>
                      <button className="btn" type="button" disabled>
                        Enter at least {MIN_SCHOOL_SEARCH_LENGTH} characters to search
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
            {!!schoolNameError?.message && <div className="invalid-feedback"> {schoolNameError.message}</div>}
          </div>
          {shouldShowNoMatchesMessage && (
            <div className="col col-12">
              <p className="small m-0">
                <strong>No matching schools were found in the database.</strong>
                <br />
                Please modify your search to locate an existing school or submit a helpdesk ticket to add your school to
                the database.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
